'use client';

import { useRouter } from '@bprogress/next';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import { SearchField } from '@ui/faqSearchInput';
import ChatModal from '@c/modals/ChatModal';
import RequestCallModal from '@c/modals/RequestCallModal';
import DynamicToast from '@c/toasts/DynamicToast';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { contactUs } from '@util/firestore/messages';
import { getCdnUrl } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Mail } from '@i/mail'
import { Chat } from '@i/chat'

const HelpCategoryCard = ({
  title,
  description,
  onClick,
  buttonText,
  href,
  icon
}: {
  title: string;
  description: string;
  onClick?: () => void;
  href?: string;
  buttonText: string;
  icon: React.ReactElement
}) => (
  <div className="flex w-full flex-col items-center gap-12 rounded-[16px] px-12 py-20 shadow-lg">
    {icon}
    <div className="flex flex-col items-center gap-8">
      <p className="text-center text-heading-h6 font-bold lg:text-heading-h4">
        {title}
      </p>
      <p className="text-center text-body-md font-medium text-text-muted">
        {description}
      </p>
    </div>
    <Button type="secondary" onClick={onClick} text={buttonText} href={href} />
  </div>
);

const checkoutInfoFormSchema = z.object({
  email: z.string().email(),
  name: z.string(),
  message: z.string(),
  phone: z.string(),
});

export type ContactUsForm = z.infer<typeof checkoutInfoFormSchema>;

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const { userDoc } = useAuth();
  const { showToast } = useToastContext();
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ContactUsForm>({
    resolver: zodResolver(checkoutInfoFormSchema),
  });

  useEffect(() => {
    if (userDoc?.email) setValue('email', userDoc?.email);
    if (userDoc?.name) setValue('name', userDoc?.name);
  }, [userDoc]);

  async function onSubmit(data: ContactUsForm) {
    try {
      if (data.message) {
        setLoading(true);
        await contactUs(data);
        setLoading(false);
        showToast(<DynamicToast text={'Message sent'} />, {
          position: 'bottom-center',
          style: {
            borderRadius: '16px',
            backgroundColor: '#FDF4E9',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#EF9021',
            width: 'min-content',
          },
        });
        reset();
      }
    } catch (e: any) {
      console.error(e);
      showToast(
        <DynamicToast
          text={
            'Failed to send. Please email support support@motorsportlocker.com'
          }
          error
        />,
        {
          position: 'bottom-center',
          style: {
            borderRadius: '16px',
            backgroundColor: '#FDF4E9',
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: '#EF9021',
            width: 'min-content',
          },
        }
      );
    }
  }
  const [showForm, setShowForm] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const router = useRouter();
  return (
    <>
      <ChatModal
        isOpen={showChatModal}
        dismiss={() => setShowChatModal(false)}
        recipientUid={process.env.NEXT_PUBLIC_SUPPORT_ID ?? ''}
      />

      <RequestCallModal
        isOpen={showCallModal}
        dismiss={() => setShowCallModal(false)}
      />

      <div className="relative h-[400px] w-full">
        <SafeImage
          src={getCdnUrl('contact/banner.jpg')}
          alt="header image"
          className="hidden h-full w-full object-cover md:block"
        />
        <SafeImage
          src={getCdnUrl('contact/banner-mobile.jpg')}
          alt="header image"
          className="h-full w-full object-cover md:hidden"
        />
        <div className="absolute top-1/2 flex w-full -translate-y-1/2 flex-col items-center px-8">
          <h1 className="mb-12 text-heading-h4 font-bold text-white lg:mb-20 lg:text-heading-h2">
            How can we help you?
          </h1>
          <SearchField />
        </div>
      </div>

      <div className="mx-auto flex max-w-content flex-col gap-20 px-8 py-20 lg:gap-32 lg:px-60 lg:py-32">
        <div className="flex flex-col items-center gap-4 lg:gap-8">
          <h1 className="text-center text-heading-h5 font-bold lg:text-heading-h3">
            Contact Us
          </h1>
          <p className="max-w-[800px] text-center text-body-lg font-medium">
            Didn’t find the answer you were looking for? Our team at Motorsport
            Locker is here to help. Reach out anytime using any of the contact
            methods below — we’ll make sure you’re taken care of.
          </p>
        </div>
        {!showForm ? (
          <div className="grid gap-12 sm:grid-cols-2">
            <HelpCategoryCard
              title="Chat with us"
              description="If you have a Motorsport Locker account, chat with our customer support team available 10am-5pm EST (Mon-Fri)."
              onClick={() => {
                if (userDoc) {
                  setShowChatModal(true);
                } else {
                  router.push('/login?redirect=/contact');
                }
              }}
              buttonText="Chat Now"
              icon={<Chat />}
            />
            <HelpCategoryCard
              title="Send us an email"
              description="Need help with an order or account? Send us an email and we’ll get back to you as soon as possible."
              buttonText="Send Email"
              onClick={() => setShowForm(true)}
              icon={<Mail />}
            />
          </div>
        ) : (
          <form
            className="mx-auto mt-[3rem] flex w-full max-w-[40rem] flex-col gap-16"
            onSubmit={handleSubmit(onSubmit)}
          >
            <FormLabel value="Email" required>
              <FormInput
                type="email"
                placeholder="Enter your email"
                error={!!errors.email}
                {...register('email')}
                autoComplete="email"
              />
            </FormLabel>
            <FormLabel value="Name" required>
              <FormInput
                type="text"
                placeholder="Enter your name"
                error={!!errors.name}
                {...register('name')}
                autoComplete="name"
              />
            </FormLabel>
            <FormLabel value="Phone" required>
              <FormInput
                type="tel"
                placeholder="Enter your phone number"
                error={!!errors.phone}
                {...register('phone')}
                autoComplete="tel"
              />
            </FormLabel>
            <FormLabel value="Message" required>
              <FormTextarea {...register('message')} error={!!errors.message} />
            </FormLabel>
            <div className="mx-auto flex flex-col justify-between gap-4 sm:flex-row">
              <Button
                text="Send Email"
                type="secondary"
                buttonType="submit"
                loading={loading}
              />
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default ContactUs;
